import { getReportMetricKind, getReportTotalColors, getReportTableSx } from '../../views/shared/report-table-styles';
import React, { useMemo, useContext, useRef, useId, useState } from 'react';
import { Box, Tooltip, Typography, Paper, useMediaQuery, Chip, Collapse, IconButton } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { useTheme, ThemeProvider } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import CurrencyFormat from 'react-currency-format';
import { alpha } from '@mui/material/styles';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { calculateTotals, lightenHexColor } from '../../dwi-functions';
import { useToken } from '../../Login/TokenProvider';
import { AppVarsContext } from '../../context/AppVarsContext';
import { resolveShowCost } from '../report-visibility';


const fontSize = "12px";

const TableCard = (props) => {
    // Stable per-instance key so multiple tables in hidden tabs don't collide
    const uidRef = useRef(useId());
    const instanceKey = useMemo(() => (props.instanceKey || props.title || uidRef.current), [props.instanceKey, props.title]);
    const compact = Boolean(props.compact);
    const [expanded, setExpanded] = useState(props.defaultExpanded !== false);


    const token = useToken();
    const admintoken = token.token.token;
    const { appVars } = useContext(AppVarsContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const branded = props.branded !== false;
    const shellBorder = `1px solid ${alpha(theme.palette.primary.main, 0.14)}`;



    // Normalize data once for safe length checks
    const dataArray = Array.isArray(props.data) ? props.data : [];

    // Helper to get tooltips
    const getTip = (field) =>
        appVars && appVars.tooltips
            ? JSON.parse(appVars.tooltips)[field] || ""
            : "";

    // Calculate totals
    const totals = useMemo(() => calculateTotals(props.data), [props.data]);

    // Build columns (migrated from your code)
    const columns = useMemo(() => {
        let cols = [...(props.columns || [])];

        //  console.log('Admin Token:', admintoken);
        // console.log('Table Card Data:', props.data);

        if (props.appendDefaultMetrics === false) {
            return cols;
        }

        // Add your dynamic columns
        if (resolveShowCost(props.reportProfile)) {
            cols = [
                ...cols,
                {
                    Header: (
                        <Tooltip title={<div><Typography fontWeight={900}>Impressions:</Typography><Typography>{getTip("impression")}</Typography></div>}>
                            <span>Impressions</span>
                        </Tooltip>
                    ),
                    header: "Impressions",
                    accessorKey: 'IMPRESSIONS',
                    grow: true,
                    maxSize: 100,
                    Cell: ({ cell }) => <NumericFormat decimalScale={0} thousandSeparator="," value={cell.getValue()} displayType="text" />,
                    Footer: () => <NumericFormat decimalScale={0} thousandSeparator="," value={totals[0]} displayType="text" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
                {
                    Header: (
                        <Tooltip title={<div><Typography fontWeight={900}>Clicks:</Typography><Typography>{getTip("click")}</Typography></div>}>
                            <span>Clicks</span>
                        </Tooltip>
                    ),
                    header: "Clicks",
                    accessorKey: 'CLICKS',
                    grow: true,
                    maxSize: 100,
                    Cell: ({ cell }) => <NumericFormat decimalScale={0} thousandSeparator="," value={cell.getValue()} displayType="text" />,
                    Footer: () => <NumericFormat decimalScale={0} thousandSeparator="," value={totals[1]} displayType="text" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
                {
                    Header: (
                        <Tooltip title={<div><Typography fontWeight={900}>CTR (click-through rate):</Typography><Typography>{getTip("ctr")}</Typography></div>}>
                            <span>CTR</span>
                        </Tooltip>
                    ),
                    header: "CTR",
                    accessorKey: 'CTR',
                    grow: true,
                    maxSize: 100,
                    Cell: ({ cell }) => <NumericFormat decimalScale={2} value={cell.getValue()} displayType="text" suffix="%" />,
                    Footer: () => <NumericFormat decimalScale={2} value={totals[1] / (totals[0] || 1) * 100} displayType="text" suffix="%" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
                {
                    Header: (
                        <Tooltip title={<div><Typography fontWeight={900}>Conversions:</Typography><Typography>{getTip("conversion")}</Typography></div>}>
                            <span>Conversions</span>
                        </Tooltip>
                    ),
                    header: "Conversions",
                    accessorKey: 'CONVERSIONS',
                    grow: true,
                    maxSize: 100,
                    Cell: ({ cell }) => <NumericFormat thousandSeparator="," decimalScale={0} value={cell.getValue()} displayType="text" />,
                    Footer: () => <NumericFormat decimalScale={0} thousandSeparator="," value={totals[3]} displayType="text" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
                {
                    Header: (
                        <Tooltip title={<div><Typography fontWeight={900}>Conversions Rate:</Typography><Typography>{getTip("conv_rate")}</Typography></div>}>
                            <span>Conv %</span>
                        </Tooltip>
                    ),
                    header: "Conv %",
                    accessorKey: 'CONV_RATE',
                    grow: true,
                    maxSize: 100,
                    Cell: ({ cell }) => <NumericFormat decimalScale={2} value={cell.getValue()} displayType="text" suffix="%" />,
                    Footer: () => <NumericFormat decimalScale={2} value={totals[3] / (totals[1] || 1) * 100} displayType="text" suffix="%" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
                {
                    Header: (
                        <Tooltip title={<div><Typography fontWeight={900}>Revenue:</Typography><Typography>{getTip("revenue")}</Typography></div>}>
                            <span>Revenue</span>
                        </Tooltip>
                    ),
                    header: "Revenue",
                    accessorKey: 'REVENUE',
                    grow: true,
                    maxSize: 100,
                    Cell: ({ cell }) => <CurrencyFormat thousandSeparator="," value={parseFloat(cell.getValue()).toFixed(2)} decimalScale={2} displayType="text" prefix="$" />,
                    Footer: () => <NumericFormat decimalScale={2} thousandSeparator="," value={parseFloat(totals[5]).toFixed(2)} displayType="text" prefix="$" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
                {
                    Header: (
                        <Tooltip title={<div><Typography fontWeight={900}>Average Transaction:</Typography><Typography>{getTip("avg_gift")}</Typography></div>}>
                            <span>Avg Transaction</span>
                        </Tooltip>
                    ),
                    header: "Avg Transaction",
                    accessorKey: 'avgGift',
                    grow: true,
                    maxSize: 100,
                    Cell: ({ cell }) => <NumericFormat thousandSeparator="," decimalScale={2} value={parseFloat(cell.getValue()).toFixed(2)} displayType="text" prefix="$" />,
                    Footer: () => <NumericFormat decimalScale={2} thousandSeparator="," value={totals[5] / (totals[3] || 1)} displayType="text" prefix="$" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
                {
                    Header: (
                        <Tooltip title={<div><Typography fontWeight={900}>CPC (cost per click):</Typography><Typography>{getTip("cpc")}</Typography></div>}>
                            <span>CPC</span>
                        </Tooltip>
                    ),
                    header: "CPC",
                    accessorKey: 'CPC',
                    grow: true,
                    maxSize: 100,
                    Cell: ({ cell }) => <NumericFormat thousandSeparator="," value={cell.getValue()} displayType="text" decimalScale={2} prefix="$" />,
                    Footer: () => <NumericFormat decimalScale={2} thousandSeparator="," value={totals[7]} displayType="text" prefix="$" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
                {
                    Header: (
                        <Tooltip title={<div><Typography fontWeight={900}>CPM (cost per thousand impressions):</Typography><Typography>{getTip("cpm")}</Typography></div>}>
                            <span>CPM</span>
                        </Tooltip>
                    ),
                    header: "CPM",
                    accessorKey: 'CPM',
                    grow: true,
                    maxSize: 100,
                    Cell: ({ cell }) => <NumericFormat thousandSeparator="," value={cell.getValue()} decimalScale={2} displayType="text" prefix="$" />,
                    Footer: () => <NumericFormat decimalScale={2} thousandSeparator="," value={totals[8]} displayType="text" prefix="$" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
                {
                    Header: (
                        <Tooltip title={<div><Typography fontWeight={900}>Cost</Typography><Typography>{getTip("cost")}</Typography></div>}>
                            <span>Cost</span>
                        </Tooltip>
                    ),
                    header: "Cost",
                    accessorKey: 'COST',
                    grow: true,
                    maxSize: 100,
                    Cell: ({ cell }) => <NumericFormat thousandSeparator="," value={cell.getValue()} decimalScale={2} displayType="text" prefix="$" />,
                    Footer: () => <NumericFormat decimalScale={2} thousandSeparator="," value={totals[9]} displayType="text" prefix="$" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
                {
                    Header: (
                        <Tooltip title={<div><Typography fontWeight={900}>ROAS (return on ad spend):</Typography><Typography>{getTip("roas")}</Typography></div>}>
                            <span>ROAS</span>
                        </Tooltip>
                    ),
                    header: "ROAS",
                    accessorKey: 'ROAS',
                    grow: true,
                    maxSize: 100,
                    Cell: ({ cell }) => <NumericFormat thousandSeparator="," decimalScale={2} value={cell.getValue()} displayType="text" />,
                    Footer: () => <NumericFormat decimalScale={2} value={totals[10]} displayType="text" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
            ];
        } else {
            cols = [
                ...cols,
                {
                    header: (
                        <Tooltip title={<div><Typography fontWeight={900}>Impressions:</Typography><Typography>{getTip("impression")}</Typography></div>}>
                            <span>Impressions</span>
                        </Tooltip>
                    ),
                    accessorKey: 'IMPRESSIONS',
                    Cell: ({ cell }) => <NumericFormat decimalScale={0} thousandSeparator="," value={cell.getValue()} displayType="text" />,
                    Footer: () => <NumericFormat decimalScale={0} thousandSeparator="," value={totals[0]} displayType="text" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
                {
                    header: (
                        <Tooltip title={<div><Typography fontWeight={900}>Clicks:</Typography><Typography>{getTip("click")}</Typography></div>}>
                            <span>Clicks</span>
                        </Tooltip>
                    ),
                    accessorKey: 'CLICKS',
                    Cell: ({ cell }) => <NumericFormat decimalScale={0} thousandSeparator="," value={cell.getValue()} displayType="text" />,
                    Footer: () => <NumericFormat decimalScale={0} thousandSeparator="," value={totals[1]} displayType="text" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
                {
                    header: (
                        <Tooltip title={<div><Typography fontWeight={900}>CTR (click-through rate):</Typography><Typography>{getTip("ctr")}</Typography></div>}>
                            <span>CTR</span>
                        </Tooltip>
                    ),
                    accessorKey: 'CTR',
                    Cell: ({ cell }) => <NumericFormat decimalScale={2} value={cell.getValue()} displayType="text" suffix="%" />,
                    Footer: () => <NumericFormat decimalScale={2} value={totals[1] / (totals[0] || 1) * 100} displayType="text" suffix="%" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
                {
                    header: (
                        <Tooltip title={<div><Typography fontWeight={900}>Conversions:</Typography><Typography>{getTip("conversion")}</Typography></div>}>
                            <span>Conversions</span>
                        </Tooltip>
                    ),
                    accessorKey: 'CONVERSIONS',
                    Cell: ({ cell }) => <NumericFormat thousandSeparator="," decimalScale={0} value={cell.getValue()} displayType="text" />,
                    Footer: () => <NumericFormat decimalScale={0} thousandSeparator="," value={totals[3]} displayType="text" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
                {
                    header: (
                        <Tooltip title={<div><Typography fontWeight={900}>Conversions Rate:</Typography><Typography>{getTip("conv_rate")}</Typography></div>}>
                            <span>Conv %</span>
                        </Tooltip>
                    ),
                    accessorKey: 'CONV_RATE',
                    Cell: ({ cell }) => <NumericFormat decimalScale={2} value={cell.getValue()} displayType="text" suffix="%" />,
                    Footer: () => <NumericFormat decimalScale={2} value={totals[3] / (totals[1] || 1) * 100} displayType="text" suffix="%" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
                {
                    header: (
                        <Tooltip title={<div><Typography fontWeight={900}>Revenue:</Typography><Typography>{getTip("revenue")}</Typography></div>}>
                            <span>Revenue</span>
                        </Tooltip>
                    ),
                    accessorKey: 'REVENUE',
                    Cell: ({ cell }) => <CurrencyFormat thousandSeparator="," value={parseFloat(cell.getValue()).toFixed(2)} decimalScale={2} displayType="text" prefix="$" />,
                    Footer: () => <NumericFormat decimalScale={2} thousandSeparator="," value={parseFloat(totals[5]).toFixed(2)} displayType="text" prefix="$" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
                {
                    header: (
                        <Tooltip title={<div><Typography fontWeight={900}>Average Transaction:</Typography><Typography>{getTip("avg_gift")}</Typography></div>}>
                            <span>Avg Transaction</span>
                        </Tooltip>
                    ),
                    accessorKey: 'avgGift',
                    Cell: ({ cell }) => <NumericFormat thousandSeparator="," decimalScale={2} value={parseFloat(cell.getValue()).toFixed(2)} displayType="text" prefix="$" />,
                    Footer: () => <NumericFormat decimalScale={2} thousandSeparator="," value={totals[5] / (totals[3] || 1)} displayType="text" prefix="$" />,
                    muiTableFooterCellProps: {
                        sx: {
                            background: theme.palette.primary.main,
                            color: getReportTotalColors(theme).color,
                            fontWeight: 900,
                            textAlign: 'center',
                            fontSize: fontSize,
                            padding: theme.spacing(.5),
                            border: `1px solid ${lightenHexColor(theme.palette.primary.main, 0)}`,
                        },
                    },
                },
            ];
        }

        const primaryColumnId = cols[0]?.accessorKey || cols[0]?.id || null;
        const getColumnId = (col) => col.accessorKey || col.id || col.header || col.title;

        // All columns: uniform header/body styling (optional, edit as needed)
        return cols.map((col) => {
            const columnId = getColumnId(col);
            const isPrimary = primaryColumnId && columnId === primaryColumnId;
            return ({
            ...col,
            header: col.header || col.title,
            muiTableHeadCellProps: {
                sx: {
                    background: theme.palette.background.default,
                    color: theme.palette.text.primary,
                    fontSize: fontSize,
                    padding: theme.spacing(.5),
                    border: `1px solid ${theme.palette.divider}`,
                    textAlign: isPrimary ? 'left' : 'right',
                    whiteSpace: isPrimary ? 'normal' : 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                },
            },
            muiTableBodyCellProps: ({ cell, column }) => {
                // console.log('Column id:', column.id);


                let color = 'transparent';
                let border = `1px solid ${theme.palette.divider}`;
                switch (column.id) {
                    case 'CLICKS':
                    case 'CTR':
                        color = `${theme.palette.primary.main}40`;
                        border = `1px solid ${theme.palette.divider}`
                        break;

                    case 'CONVERSIONS':
                    case 'CONV_RATE':
                        color = `${theme.palette.primary.main}80`;
                        border = `1px solid ${theme.palette.divider}`
                        break;

                    default:
                        break;
                }



                const isPrimaryCol = primaryColumnId && (column.id === primaryColumnId || column.accessorKey === primaryColumnId);
                return ({
                    'data-report-metric': getReportMetricKind(column.id) || getReportMetricKind(col.header),
                        sx: {
                        background: color,
                        fontWeight: 900,
                        textAlign: isPrimaryCol ? 'left' : 'right',
                        fontSize: fontSize,
                        padding: theme.spacing(.25),
                        border: border,
                        whiteSpace: isPrimaryCol ? 'normal' : 'nowrap',
                        wordBreak: isPrimaryCol ? 'break-word' : 'normal',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }
                })
            },
            muiTableFooterCellProps: {
                sx: {
                    textAlign: 'right',
                    padding: theme.spacing(.5),
                    border: `1px solid ${theme.palette.divider}`,
                    fontSize: fontSize,
                    fontWeight: 900,
                    background: theme.palette.primary.main,
                    color: getReportTotalColors(theme).color,
                },
            },
            // muiTableFooterCellProps handled above or default empty if none
        })
        });
    }, [props.columns, theme, appVars, admintoken, props.reportProfile, totals]);

    const getColumnLabel = (col) => {
        const raw = typeof col.header === 'string'
            ? col.header
            : (typeof col.Header === 'string' ? col.Header : (typeof col.title === 'string' ? col.title : (col.accessorKey || col.id || '')));
        if (!raw) return 'Value';
        const normalized = String(raw).replace(/_/g, ' ').trim();
        if (/^[A-Z0-9\s]+$/.test(normalized)) {
            const compact = normalized.replace(/\s+/g, '');
            if (compact.length <= 4) return normalized;
            return normalized.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
        }
        return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const getCellValue = (row, col) => {
        if (typeof col.accessorFn === 'function') return col.accessorFn(row);
        if (col.accessorKey && row && Object.prototype.hasOwnProperty.call(row, col.accessorKey)) {
            return row[col.accessorKey];
        }
        if (col.id && row && Object.prototype.hasOwnProperty.call(row, col.id)) {
            return row[col.id];
        }
        return null;
    };

    const renderCellValue = (row, col) => {
        const cellValue = getCellValue(row, col);
        if (typeof col.Cell === 'function') {
            const rendered = col.Cell({
                cell: { getValue: () => cellValue, row: { original: row } },
                row: { original: row },
                column: { id: col.accessorKey || col.id }
            });
            return rendered;
        }
        if (cellValue == null || cellValue === '') return '—';
        return String(cellValue);
    };

    const defaultColumn = useMemo(() => ({
        minSize: 80,
        size: 120,
        maxSize: 320
    }), []);

    return (
        <ThemeProvider theme={theme}>
            <Paper
                elevation={0}
                sx={branded ? {
                    position: 'relative',
                    overflow: 'hidden',
                    width: '100%',
                    borderRadius: 3,
                    border: shellBorder,
                    background: alpha('#ffffff', 0.8),
                    backdropFilter: 'blur(14px)',
                    boxShadow: 'none',
                } : {
                    width: '100%',
                    boxShadow: 'none',
                    background: 'transparent',
                }}
            >
                <Box
                    sx={{
                        p: branded ? { xs: 1.5, md: 2 } : 0,
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr)',
                        gap: branded ? 1.5 : 0,
                        ...getReportTableSx(theme),
                    }}
                >
                    {(props.title || branded) ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                px: branded ? 0 : { xs: 1.5, md: 2 },
                                py: branded ? 0 : 1.5,
                                minHeight: 56,
                                justifyContent: 'space-between',
                                gap: 1.5,
                                flexWrap: 'wrap'
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                {props.title ? (
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                        {props.title}
                                    </Typography>
                                ) : (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em' }}>
                                        Performance Table
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 'auto' }}>
                                <Chip
                                    size="small"
                                    label={`${dataArray.length} row${dataArray.length === 1 ? '' : 's'}`}
                                    sx={{
                                        borderRadius: 999,
                                        fontWeight: 700,
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        borderColor: alpha(theme.palette.primary.main, 0.14),
                                    }}
                                    variant="outlined"
                                />
                                {props.collapsible !== false ? (
                                    <IconButton
                                        size="small"
                                        onClick={() => setExpanded((current) => !current)}
                                        aria-label={expanded ? 'Collapse table' : 'Expand table'}
                                    >
                                        {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                    </IconButton>
                                ) : null}
                            </Box>
                        </Box>
                    ) : null}

                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                {isMobile ? (
                    <Box sx={{ display: 'grid', gap: 1.5 }}>
                        {dataArray.map((row, index) => {
                            const primaryCol = columns[0];
                            const primaryValue = primaryCol ? renderCellValue(row, primaryCol) : `Row ${index + 1}`;
                            const detailColumns = columns.slice(1);
                            return (
                                <Paper key={`mrt-card-${instanceKey}-${index}`} variant="outlined" sx={{ p: 1.25 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                                            {primaryValue || `Row ${index + 1}`}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
                                        {detailColumns.map((col, colIndex) => (
                                            <Box data-report-metric={getReportMetricKind(col.accessorKey || col.id) || getReportMetricKind(col.header)} key={`mrt-card-${instanceKey}-${index}-${colIndex}`} sx={{ display: 'grid', gap: 0.25 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {getColumnLabel(col)}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                                                    {renderCellValue(row, col)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                ) : (
                    <MaterialReactTable
                        key={`mrt-${instanceKey}-${String(!!props.active)}`}
                        data={dataArray}
                        getRowId={(row, i) => row.ID ?? row.id ?? row.CAMPAIGN_ID ?? `${instanceKey}-${i}`}
                        defaultColumn={defaultColumn}
                        layoutMode="semantic"
                        enableRowVirtualization={false}
                        muiTableContainerProps={{
                            sx: {
                                overflowX: 'auto',
                                width: '100%',
                                maxWidth: '100%',
                                display: 'block',
                                willChange: 'transform',
                                ...(compact ? { maxHeight: '70vh' } : {})
                            }
                        }}
                        muiTableProps={{
                            sx: {
                                width: '100%',
                                tableLayout: 'auto',
                                minWidth: 0
                            }
                        }}
                        columns={columns}
                        // enableBottomToolbar={false}
                        enableTopToolbar={false}
                        enableFullScreenToggle={false}
                        enableColumnResizing={false}
                        enableFilters={false}
                        enableGlobalFilter={false}
                        enableDensityToggle={false}
                        enableGrouping={false}
                        enableHiding={false}
                        enableRowSelection={false}
                        enableColumnOrdering={false}
                        enableBottomToolbar={dataArray.length > 10}
                        // ✅ Pagination only if more than 10 rows
                        enablePagination={dataArray.length > 10}
                        // ✅ Page size defaults to 10 if more than 10, else use the actual length

                        // Helps when rendering inside display:none tabs; MRT measures on mount
                        state={{}}
                        initialState={compact ? { density: 'compact' } : {}}
                        muiTableHeadCellProps={{
                            sx: {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                ...(compact ? { px: 1, py: 0.75, fontSize: '12px' } : {})
                            }
                        }}
                        muiTableBodyCellProps={({ column }) => ({
                            'data-report-metric': getReportMetricKind(column.id) || getReportMetricKind(column.columnDef.header),
                            sx: {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                ...(compact ? { px: 1, py: 0.75, fontSize: '12px' } : {})
                            }
                        })}
                        muiTableFooterCellProps={{
                            align: 'right',
                            sx: {
                                textAlign: 'center',
                                p: .5,
                                m: 0,
                                alignItems: 'center'
                            }
                        }}
                        muiTablePaperProps={{
                            elevation: 0,
                            sx: branded
                                ? {
                                    border: shellBorder,
                                    borderRadius: 3,
                                    width: '100%',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    background: alpha('#ffffff', 0.74),
                                    backdropFilter: 'blur(14px)',
                                    boxShadow: 'none',
                                }
                                : { border: 0, width: '100%', maxWidth: '100%', overflow: 'hidden' },
                        }}
                        muiTableBodyRowProps={({ row }) => ({
                            sx: {
                                textAlign: 'center',
                                backgroundColor: branded
                                    ? (row.index % 2 === 0 ? alpha('#ffffff', 0.86) : alpha(theme.palette.primary.main, 0.06))
                                    : (row.index % 2 === 0 ? '#fff' : theme.palette.action.hover),
                            },
                        })}
                        renderDetailPanel={props.renderDetailPanel}
                        enableExpandAll={false}
                    />
                )}
                    </Collapse>
                </Box>
            </Paper>
        </ThemeProvider>
    );
};

export default TableCard;
